import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router';
import { z } from 'zod';
import { ROLES } from '@bekebi/domain';
import { Button, buttonClass } from '../../components/button';
import { roleLabel } from '../../session/roles';
import { useCreateUser } from './api';
import { LinkIssueResult } from './link-result';

/**
 * Add user (/app/users/new) — POST /api/users with exactly the fields the API accepts: email and
 * role. The portal emails the person an invitation link and they choose their own password; the
 * Admin never sets or sees it (DECISIONS.md A-AC1). The Bekebi User ID is generated by the portal.
 * The outcome is shown here, so a link returned because its email did not go out stays only in
 * this page's memory (never in the URL or the browser history).
 */
const schema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter an email address.')
    .max(254, 'The email address can be at most 254 characters.')
    .email('Enter a valid email address.'),
  role: z.enum(ROLES, { message: 'Choose a role.' }),
});
type Fields = z.input<typeof schema>;

const linkClass =
  'text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link';

export function UserCreatePage() {
  const create = useCreateUser();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Fields>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', role: '' as Fields['role'] },
  });

  const onSubmit = handleSubmit((values) =>
    create.mutate({ email: values.email.trim(), role: values.role }),
  );

  const field =
    'mt-1 block w-full max-w-xl rounded-md border border-line-strong bg-surface px-3 py-2 text-sm';

  if (create.isSuccess) {
    const { user, invitation } = create.data;
    return (
      <div className="max-w-xl">
        <p className="text-sm">
          <Link to="/app/users" className={linkClass}>
            Users
          </Link>
        </p>
        <h1 className="mt-2">User added</h1>
        <p className="mt-1 text-sm text-muted">
          {user.email} was added as {roleLabel(user.role)} with Bekebi User ID {user.bekebiUserId}.
          They appear as Invited until they choose their password.
        </p>
        <div className="mt-6">
          <LinkIssueResult email={user.email} result={invitation} />
        </div>
        <div className="mt-6 flex gap-3">
          <Link to="/app/users" className={buttonClass('primary')}>
            Back to Users
          </Link>
          <Button variant="secondary" onClick={() => create.reset()}>
            Add another user
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl">
      <p className="text-sm">
        <Link to="/app/users" className={linkClass}>
          Users
        </Link>
      </p>
      <h1 className="mt-2">Add user</h1>
      <p className="mt-1 text-sm text-muted">
        Give someone access to the Bekebi Portal. The portal emails them an invitation with a link
        that works once, for 72 hours, to choose their own password. Nobody else ever knows it.
        Their Bekebi User ID is assigned by the portal.
      </p>
      <form onSubmit={onSubmit} noValidate aria-label="Add user" className="mt-6 space-y-5">
        <div>
          <label htmlFor="user-email" className="block text-sm font-medium text-fg-2">
            Email
          </label>
          <input
            id="user-email"
            type="email"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'user-email-error' : 'user-email-hint'}
            className={field}
            {...register('email')}
          />
          <p id="user-email-hint" className="mt-1 text-xs text-muted">
            The invitation goes to this address, so it must be one only this person can read.
          </p>
          {errors.email ? (
            <p id="user-email-error" className="mt-1 text-sm text-danger">
              {errors.email.message}
            </p>
          ) : null}
        </div>
        <div>
          <label htmlFor="user-role" className="block text-sm font-medium text-fg-2">
            Role
          </label>
          <select
            id="user-role"
            aria-invalid={errors.role ? 'true' : 'false'}
            aria-describedby={errors.role ? 'user-role-error' : 'user-role-hint'}
            className="mt-1 block min-h-10 w-full max-w-xl rounded-md border border-line-strong bg-surface px-3 text-sm"
            {...register('role')}
          >
            <option value="">Choose a role…</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </select>
          <p id="user-role-hint" className="mt-1 text-xs text-muted">
            Admin: full technical and management view. ICT: the operational view.
          </p>
          {errors.role ? (
            <p id="user-role-error" className="mt-1 text-sm text-danger">
              {errors.role.message}
            </p>
          ) : null}
        </div>
        {create.isError ? (
          <p role="alert" className="text-sm text-danger">
            {create.error.message}
          </p>
        ) : null}
        <div className="flex gap-3">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add user and send invitation'}
          </Button>
          <Link
            to="/app/users"
            className="inline-flex min-h-10 items-center rounded-md border border-line-strong bg-surface px-4 text-sm font-medium text-fg hover:bg-raised"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
